import { useEffect, useState } from "react";
import { Badge, Flex, Pagination, Table, TableColumnsType, TableProps } from "antd";
import { mdlReservation } from "../../models/domain/Reservation";
import { useTranslation } from "react-i18next";
import { enmConfirmationStatus } from "../../models/enums/ConfirmationStatus";
import { enmReservationType } from "../../models/enums/ReservationType";
import { enmReservationStatus } from "../../models/enums/ReservationStatus";
import _ from "lodash";
import { mdlTablePagination } from "../../models/ui-models/TablePagination";
import dayjs from "dayjs";
import $ from 'jquery';
import { FilterValue, Key, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/es/table/interface";
import { useAppSelector } from "../../store/Hooks";
import { enmQuotaType } from "../../models/enums/QuotaType";
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { mdlReservationListRequest } from "../../models/service-models/reservation/ReservationListRequest";
import { handleTableAgencyFilter, handleTableDateFilter, handleTableFilter, handleTableHotelFilter, handleTableSort } from "../common/TableFilter";
import { enmSortDirection } from "../../models/enums/SortDirection";
import { ContextMenuPosition, ReservationContextMenu } from "./ReservationContextMenu";

const MAX_SELECTION = 100;

export type ReservationMonitorTableProps = {
  dataSource?: mdlReservation[],
  onTableParamsChange?: (request: mdlReservationListRequest) => void;
  onPaginationChange?: Function;
  pagination: mdlTablePagination;
  expandRowRenderer?: Function;
  onRowDoubleClicked?: Function;
  onRowSelection?: Function;
  selectedRowKeys?: string[];
  selections?: mdlReservation[];
  tableTools?: JSX.Element;
  reservationListRequest?: mdlReservationListRequest;
  onContextMenuItemClick?: (menuKeyId?: string, reservation?: mdlReservation) => void;
}

export const ReservationMonitorTable = (props: ReservationMonitorTableProps) => {
  const { t } = useTranslation();
  const tableColumns = useAppSelector(x => x.site.resMonColumns);
  const [source, setSource] = useState<mdlReservation[]>();
  const userHotels = useAppSelector(x => x.site.userHotels);
  const crossAgencies = useAppSelector(x => x.site.crossAgencies);
  const [isContextMenuVisible, setContextMenuVisibility] = useState<boolean>(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<ContextMenuPosition>({ x: 0, y: 0 });
  const [contextMenuReservation, setContextMenuReservation] = useState<mdlReservation>({});
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    if (props.dataSource) {
      var data = _.cloneDeep(props.dataSource);
      _.each(data, function (d, i) {
        d.key = i;
        _.each(d.customers, function (customer, cIndex) {
          customer.key = cIndex;
        })
      })
      setSource(data);
    }
  }, [props.dataSource, tableColumns]);

  const getColumnHideStatus = (columnName: string) => {
    if (!tableColumns)
      return false;
    if (tableColumns != null && tableColumns.includes(columnName))
      return false;
    else
      return true;
  }

  const baseColumns: TableColumnsType<mdlReservation> = [
    {
      title: t("resTable.hotelConfirmationStatus"),
      key: 'confirmationStatus',
      hidden: getColumnHideStatus("confirmationStatus"),
      filters: [
        { text: t("resTable.confirmationStatusNo"), value: enmConfirmationStatus.No },
        { text: t("resTable.confirmationStatusWait"), value: enmConfirmationStatus.Wait },
        { text: t("resTable.confirmationStatusYes"), value: enmConfirmationStatus.Yes },
      ],
      render: (e: mdlReservation) => {
        switch (e.confirmationStatus) {
          case enmConfirmationStatus.No:
            return <Badge status="error" text={t('resTable.confirmationStatusNo')} />
          case enmConfirmationStatus.Yes:
            return <Badge status="success" text={t('resTable.confirmationStatusYes')} />
          default:
            return <Badge status="processing" text={t('resTable.confirmationStatusWait')} />
        }
      },
      filtered: true,
      filteredValue: props.reservationListRequest?.confirmationStatuses,
      ...handleTableSort(props.reservationListRequest, "confirmationStatus")
    },
    {
      title: t("resTable.reservationType"),
      key: 'reservationType',
      hidden: getColumnHideStatus("reservationType"),
      filters: [
        { text: t("resTable.reservationTypeCancelled"), value: enmReservationType.Cancelled },
        { text: t("resTable.reservationTypeChanged"), value: enmReservationType.Changed },
        { text: t("resTable.reservationTypeNew"), value: enmReservationType.New },
      ],
      render: (e: mdlReservation) => {
        switch (e.reservationType) {
          case enmReservationType.Cancelled:
            return <Badge status="error" text={t('resTable.reservationTypeCancelled')} />
          case enmReservationType.Changed:
            return <Badge status="warning" text={t('resTable.reservationTypeChanged')} />
          case enmReservationType.New:
            return <Badge status="success" text={t('resTable.reservationTypeNew')} />
        }
      },
      filtered: true,
      filteredValue: props.reservationListRequest?.reservationTypes,
      ...handleTableSort(props.reservationListRequest, "reservationType")
    },
    {
      title: t("resTable.reservationStatus"),
      key: 'reservationStatus',
      hidden: getColumnHideStatus("reservationStatus"),
      filters: [
        { text: t("resTable.reservationStatusGoShow"), value: enmReservationStatus.GoShow },
        { text: t("resTable.reservationStatusNoShow"), value: enmReservationStatus.NoShow },
        { text: t("resTable.reservationStatusOnRequest"), value: enmReservationStatus.OnRequest },
        { text: t("resTable.reservationStatusOption"), value: enmReservationStatus.Option },
        { text: t("resTable.reservationStatusOk"), value: enmReservationStatus.Ok },
      ],
      render: (e: mdlReservation) => {
        switch (e.reservationStatus) {
          case enmReservationStatus.GoShow:
            return <Badge status="success" text={t('resTable.reservationStatusGoShow')} />
          case enmReservationStatus.NoShow:
            return <Badge status="error" text={t('resTable.reservationStatusNoShow')} />
          case enmReservationStatus.OnRequest:
            return <Badge status="warning" text={t('resTable.reservationStatusOnRequest')} />
          case enmReservationStatus.Option:
            return <Badge status="warning" text={t('resTable.reservationStatusOption')} />
          case enmReservationStatus.Ok:
            return <Badge status="success" text={t('resTable.reservationStatusOk')} />
        }
      },
      filtered: true,
      filteredValue: props.reservationListRequest?.reservationStatuses,
      ...handleTableSort(props.reservationListRequest, "reservationStatus")
    },
    {
      title: t("resTable.agencyName"),
      dataIndex: 'agencyName',
      key: 'agencyName',
      hidden: getColumnHideStatus("agencyName"),
      ...handleTableAgencyFilter(props.reservationListRequest?.agencyIds, crossAgencies),
      ...handleTableSort(props.reservationListRequest, "agencyName")
    },
    {
      title: t("resTable.hotelName"),
      dataIndex: 'hotelName',
      key: 'hotelName',
      hidden: getColumnHideStatus("hotelName"),
      ...handleTableHotelFilter(props.reservationListRequest?.hotelIds, userHotels),
      ...handleTableSort(props.reservationListRequest, "hotelName")
    },
    {
      title: t("resTable.sejHotelName"),
      dataIndex: 'sejHotelName',
      key: 'sejHotelName',
      hidden: getColumnHideStatus("sejHotelName"),
      ...handleTableSort(props.reservationListRequest, "sejHotelName")
    },
    {
      title: t("resTable.operator"),
      dataIndex: 'operator',
      key: 'operator',
      hidden: getColumnHideStatus("operator"),
      ...handleTableSort(props.reservationListRequest, "operator")
    },
    {
      title: t("resTable.voucherNo"),
      dataIndex: 'voucherNo',
      key: 'voucherNo',
      hidden: getColumnHideStatus("voucherNo"),
      ...handleTableFilter(props.reservationListRequest?.voucher),
      ...handleTableSort(props.reservationListRequest, "voucherNo")
    },
    {
      title: t("resTable.sellDate"),
      key: 'sellDate',
      render: (e: mdlReservation) => {
        return e.sellDate ? dayjs(e.sellDate).format("DD.MM.YYYY") : '-'
      },
      hidden: getColumnHideStatus("sellDate"),
      ...handleTableDateFilter(props.reservationListRequest?.sellBegin, props.reservationListRequest?.sellEnd),
      ...handleTableSort(props.reservationListRequest, "sellDate")
    },
    {
      title: t("resTable.confirmDate"),
      key: 'confirmDate',
      render: (e: mdlReservation) => {
        if (e.confirmDate)
          return dayjs(e.confirmDate).format("DD.MM.YYYY")
        else
          return "";
      },
      hidden: getColumnHideStatus("confirmDate"),
      ...handleTableDateFilter(props.reservationListRequest?.confirmDateBegin, props.reservationListRequest?.confirmDateEnd),
      ...handleTableSort(props.reservationListRequest, "confirmDate")
    },
    {
      title: t("resTable.confirmeNote"),
      dataIndex: 'confirmeNote',
      key: "confirmeNote",
      hidden: getColumnHideStatus("confirmeNote"),
      ...handleTableSort(props.reservationListRequest, "confirmeNote")
    },
    {
      title: t("resTable.checkIn"),
      key: 'checkIn',
      render: (e: mdlReservation) => {
        return dayjs(e.checkIn).format("DD.MM.YYYY")
      },
      hidden: getColumnHideStatus("checkIn"),
      ...handleTableDateFilter(props.reservationListRequest?.checkInBegin, props.reservationListRequest?.checkInEnd),
      ...handleTableSort(props.reservationListRequest, "checkIn")
    },
    {
      title: t("resTable.checkOut"),
      key: 'checkOut',
      render: (e: mdlReservation) => {
        return dayjs(e.checkOut).format("DD.MM.YYYY")
      },
      hidden: getColumnHideStatus("checkOut"),
      ...handleTableDateFilter(props.reservationListRequest?.checkOutBegin, props.reservationListRequest?.checkOutEnd),
      ...handleTableSort(props.reservationListRequest, "checkOut")
    },
    {
      title: t("resTable.nights"),
      dataIndex: 'nights',
      key: 'nights',
      hidden: getColumnHideStatus("nights"),
      ...handleTableSort(props.reservationListRequest, "nights")

    },
    {
      title: t("resTable.roomName"),
      dataIndex: 'roomName',
      key: 'roomName',
      hidden: getColumnHideStatus("roomName"),
      ...handleTableSort(props.reservationListRequest, "roomName")
    },
    {
      title: t("resTable.roomTypeName"),
      dataIndex: 'roomTypeName',
      key: 'roomTypeName',
      hidden: getColumnHideStatus("roomTypeName"),
      ...handleTableSort(props.reservationListRequest, "roomTypeName")
    },
    {
      title: t("resTable.accomName"),
      dataIndex: 'accomName',
      key: 'accomName',
      hidden: getColumnHideStatus("accomName"),
      ...handleTableSort(props.reservationListRequest, "accomName")
    },
    {
      title: t("resTable.roomCount"),
      dataIndex: 'roomCount',
      key: 'roomCount',
      hidden: getColumnHideStatus("roomCount"),
      ...handleTableSort(props.reservationListRequest, "roomCount")
    },
    {
      title: t("resTable.quotaType"),
      key: 'quotaType',
      render: (e: mdlReservation) => {
        switch (e.quotaType) {
          case enmQuotaType.OnRequest:
            return <Badge status="warning" text={t('resTable.quotaTypeOnRequest')} />
          case enmQuotaType.Normal:
            return <Badge status="processing" text={t('resTable.quotaTypeNormal')} />
          case enmQuotaType.Mix:
            return <Badge status="success" text={t('resTable.quotaTypeMix')} />
          case enmQuotaType.Guarantee:
            return <Badge status="error" text={t('resTable.quotaTypeGuarantee')} />
        }
      },
      hidden: getColumnHideStatus("quotaType"),
      ...handleTableSort(props.reservationListRequest, "quotaType")
    },
    {
      title: t("resTable.soldPrice"),
      dataIndex: 'soldPrice',
      key: 'soldPrice',
      hidden: getColumnHideStatus("soldPrice"),
      ...handleTableSort(props.reservationListRequest, "soldPrice")
    },
    {
      title: t("resTable.soldCurrency"),
      dataIndex: 'soldCurrency',
      key: 'soldCurrency',
      hidden: getColumnHideStatus("soldCurrency"),
      ...handleTableSort(props.reservationListRequest, "soldCurrency")
    },
    {
      title: t("resTable.hasExtras"),
      key: 'hasExtra',
      hidden: getColumnHideStatus("hasExtra"),
      render: (e: mdlReservation) => {
        if (e.hasExtra) {
          return <CheckOutlined style={{ color: "green" }} />;
        }
        else {
          return <CloseOutlined style={{ color: "red" }} />;
        }
      },
      ...handleTableSort(props.reservationListRequest, "hasExtra")
    },
    {
      title: t("resTable.originalHotelCode"),
      dataIndex: 'originalHotelCode',
      key: 'originalHotelCode',
      hidden: getColumnHideStatus("originalHotelCode"),
      ...handleTableSort(props.reservationListRequest, "originalHotelCode")
    },
    {
      title: t("resTable.adults"),
      dataIndex: 'adults',
      key: 'adults',
      hidden: getColumnHideStatus("adults"),
      ...handleTableFilter(props.reservationListRequest?.adultStart || props.reservationListRequest?.adultEnd),
      ...handleTableSort(props.reservationListRequest, "adults")
    },
    {
      title: t("resTable.childs"),
      dataIndex: 'childs',
      key: 'childs',
      hidden: getColumnHideStatus("childs"),
      ...handleTableFilter(props.reservationListRequest?.childStart || props.reservationListRequest?.childEnd),
      ...handleTableSort(props.reservationListRequest, "childs")
    },
    {
      title: t("resTable.infants"),
      dataIndex: 'infants',
      key: 'infants',
      hidden: getColumnHideStatus("infants"),
      ...handleTableFilter(props.reservationListRequest?.infantStart || props.reservationListRequest?.infantEnd),
      ...handleTableSort(props.reservationListRequest, "infants")
    },
    {
      title: t("resTable.extraBed"),
      dataIndex: 'extraBed',
      key: 'extraBed',
      hidden: getColumnHideStatus("extraBed"),
      ...handleTableSort(props.reservationListRequest, "extraBed")
    },
    {
      title: t("resTable.pax"),
      dataIndex: 'pax',
      key: 'pax',
      hidden: getColumnHideStatus("pax"),
      ...handleTableFilter(props.reservationListRequest?.totalPaxStart || props.reservationListRequest?.totalPaxEnd),
      ...handleTableSort(props.reservationListRequest, "pax")
    },
    {
      title: t("resTable.note"),
      dataIndex: 'note',
      key: 'note',
      hidden: getColumnHideStatus("note"),
      ...handleTableSort(props.reservationListRequest, "note")
    },
  ];

  const expandedRowRenderer = (voucher?: string, isExpanded?: boolean) => (
    props.expandRowRenderer && props.expandRowRenderer(voucher, isExpanded)
  );

  const handleTableChange: TableProps<mdlReservation>['onChange'] = (pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult | SorterResult[], extra: TableCurrentDataSource) => {
    if (props.onTableParamsChange) {
      const tableSortResult: SorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
      props.onTableParamsChange({
        confirmationStatuses: filters["confirmationStatus"] as enmConfirmationStatus[],
        reservationTypes: filters["reservationType"] as enmReservationType[],
        reservationStatuses: filters["reservationStatus"] as enmReservationStatus[],
        sortField: tableSortResult?.columnKey?.toString(),
        sortDirection: tableSortResult?.order ? (tableSortResult?.order === "ascend" ? enmSortDirection.Ascending : enmSortDirection.Descending) : null
      });
    }
  }

  const handleRowDoubleClick = (e: React.MouseEvent<HTMLInputElement>, res: mdlReservation) => {
    if (!$(e.target).hasClass("ant-table-row-expand-icon"))
      props.onRowDoubleClicked && props.onRowDoubleClicked(res.reservationId)
  }

  const getScreenSize = () => {
    return `${(window.screen.height * 65) / 100}px`;
  }

  const reorderColumns = (columns: TableColumnsType<mdlReservation>, order?: string[]) => {
    if (order) {
      const orderedColumns: TableColumnsType<mdlReservation> = [];
      const remainingColumns: TableColumnsType<mdlReservation> = [];

      // Create a map for quick lookup
      const orderMap = new Map(order.map((key, index) => [key, index]));

      // Separate ordered and remaining columns
      columns.forEach((col) => {
        if (orderMap.has(col.key as string)) {
          orderedColumns[orderMap.get(col.key as string)!] = col;
        } else {
          remainingColumns.push(col);
        }
      });

      // Combine ordered columns and remaining columns
      return [...orderedColumns.filter(Boolean), ...remainingColumns];
    }
    else {
      return columns;
    }
  };

  const handleRowContextMenu = (e: React.MouseEvent<HTMLInputElement>, res: mdlReservation) => {
    e.preventDefault();
    if (!isContextMenuVisible) {
      document.addEventListener(`click`, function onClickOutside() {
        setContextMenuReservation({});
        setContextMenuVisibility(false);
        document.removeEventListener(`click`, onClickOutside);
      });
    }
    setContextMenuReservation(res);
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setContextMenuVisibility(true);
  }

  const handleContextMenuClick = (menuKeyId?: string) => {
    if (props.onContextMenuItemClick) {
      props.onContextMenuItemClick(menuKeyId, contextMenuReservation);
    }
  }

  const orderedColumns = reorderColumns(baseColumns, tableColumns);

  const columnsWithRowNumber: TableColumnsType<mdlReservation> = [
    {
      title: "#",
      key: "__rowNumber",
      width: 20,
      fixed: "left",
      render: (_: any, __: mdlReservation, index: number) => {
        const page = props.pagination?.current ?? 1;
        const pageSize = props.pagination?.pageSize ?? 10;
        return (page - 1) * pageSize + index + 1;
      },
    },
    ...orderedColumns,
  ];

  // rowClassName={(record) => (record.hasWarning ? 'red-row' : '')}

  return (
    <Flex gap="middle" align="start" vertical>
      <ReservationContextMenu isOpen={isContextMenuVisible} position={contextMenuPosition} onContextMenuItemClick={handleContextMenuClick} />
      <Table<mdlReservation>
        style={{ maxHeight: getScreenSize() }}
        rowKey={(reservation: mdlReservation) => reservation.reservationId ?? ""}
        onRow={(reservation) => ({
          onDoubleClick: (e) => handleRowDoubleClick(e, reservation),
          onContextMenu: (e) => handleRowContextMenu(e, reservation)
        })}
        rowSelection={
          {
            preserveSelectedRowKeys: true,
            selectedRowKeys: props.selectedRowKeys,
            onChange: (keys: Key[], rows: mdlReservation[]) => {
              const limitedKeys = keys.slice(0, MAX_SELECTION);
              const limitedRows = rows.slice(0, MAX_SELECTION);

              props.onRowSelection && props.onRowSelection(limitedKeys, limitedRows)
            },
            getCheckboxProps: (record) => {
              const selectedKeys = props.selectedRowKeys ?? [];
              const selectedCount = selectedKeys.length;

              const recordKey = record.reservationId ?? "";
              const isSelected = selectedKeys.includes(recordKey);

              return {
                disabled: !isSelected && selectedCount >= MAX_SELECTION,
              };
            }
          }}
        onChange={handleTableChange}
        columns={columnsWithRowNumber}
        pagination={false}
        expandable={{
          expandedRowRender: (record) => (expandedRowRenderer(record.reservationId, expandedRowKeys.includes(record.reservationId ?? ""))),
          expandedRowKeys: expandedRowKeys,
          onExpandedRowsChange: (newExpandedRowKeys) => {
            setExpandedRowKeys([...newExpandedRowKeys]);
          },
        }}
        size="small"
        dataSource={source}
        scroll={{ x: 'max-content' }}
      />
      <Flex style={{ width: "100%" }} justify="space-between" wrap gap={"15px"}>
        <Pagination
          showSizeChanger
          current={props.pagination.current}
          onChange={(page: number, pageSize: number) => props.onPaginationChange && props.onPaginationChange(page, pageSize)}
          //defaultCurrent={1}
          pageSize={props.pagination.pageSize}
          total={props.pagination.total}
          showTotal={(total, range) => `${range[0]}-${range[1]} / ${total}`}
        />
        {
          props.tableTools
          &&
          props.tableTools
        }
      </Flex>

    </Flex >

  );
};